import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { classNames } from '../../utilities/styleUtils';

interface AccountSetupProgressProps {
  currentStep: number;
  mode?: 'add' | 'edit';
}

interface Step {
  id: number;
  name: string;
  description: string;
}

const addSteps: Step[] = [
  { id: 1, name: 'Validate Account', description: 'Enter and validate account identifier' },
  { id: 2, name: 'Configure Access', description: 'Run SQL commands in Snowflake' },
  { id: 3, name: 'Test Connection', description: 'Verify account connection' },
];

const editSteps: Step[] = [
  { id: 1, name: 'Edit Account', description: 'Edit and save connection details' },
  { id: 2, name: 'Configure Access', description: 'Run SQL commands in Snowflake' },
  { id: 3, name: 'Test Connection', description: 'Verify account connection' },
];

/**
 * Visual progress indicator for the account setup flow
 *
 * Features:
 * - Shows 3 main steps: "Validate Account" → "Configure Access" → "Test Connection" (add mode)
 * - Shows 3 main steps: "Edit Account" → "Configure Access" → "Test Connection" (edit mode)
 * - Highlights current step
 * - Shows completed steps with checkmarks
 * - Shows upcoming steps in muted colors
 * - Responsive (vertical on mobile, horizontal on desktop)
 *
 * @param currentStep - Current step (1, 2, or 3)
 * @param mode - Mode of operation ('add' or 'edit'), defaults to 'add'
 */
export function AccountSetupProgress({ currentStep, mode = 'add' }: AccountSetupProgressProps) {
  const steps = mode === 'edit' ? editSteps : addSteps;

  return (
    <nav aria-label="Progress" className="mb-8">
      {/* Mobile: Vertical layout */}
      <ol className="flex flex-col space-y-4 md:hidden">
        {steps.map((step) => (
          <li key={step.id} className="flex items-start">
            <div className="flex items-center">
              <div
                className={classNames(
                  'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full',
                  step.id < currentStep
                    ? 'bg-dataops-primary-light-blue'
                    : step.id === currentStep
                    ? 'border-2 border-dataops-primary-light-blue bg-white'
                    : 'border-2 border-gray-300 bg-white',
                )}
              >
                {step.id < currentStep ? (
                  <CheckCircleIcon className="h-6 w-6 text-white" aria-hidden="true" />
                ) : (
                  <span
                    className={classNames(
                      'text-sm font-medium',
                      step.id === currentStep ? 'text-dataops-primary-light-blue' : 'text-gray-500',
                    )}
                  >
                    {step.id}
                  </span>
                )}
              </div>
            </div>
            <div className="ml-4 flex min-w-0 flex-col">
              <span
                className={classNames(
                  'text-sm font-medium',
                  step.id <= currentStep ? 'text-dataops-secondary-dark-blue' : 'text-gray-500',
                )}
              >
                {step.name}
              </span>
              <span className="text-xs text-gray-500">{step.description}</span>
            </div>
          </li>
        ))}
      </ol>

      {/* Desktop: Horizontal layout */}
      <ol className="hidden md:flex md:items-center md:justify-center">
        {steps.map((step, stepIdx) => (
          <li key={step.id} className={classNames('relative', stepIdx !== steps.length - 1 ? 'pr-20' : '')}>
            {/* Connector line */}
            {stepIdx !== steps.length - 1 && (
              <div className="absolute left-12 top-5 -ml-px h-0.5 w-full" aria-hidden="true">
                <div
                  className={classNames(
                    'h-full w-full',
                    step.id < currentStep ? 'bg-dataops-primary-light-blue' : 'bg-gray-300',
                  )}
                />
              </div>
            )}

            <div className="group relative flex flex-col items-center">
              <span className="flex h-10 w-10 items-center justify-center">
                <span
                  className={classNames(
                    'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full',
                    step.id < currentStep
                      ? 'bg-dataops-primary-light-blue'
                      : step.id === currentStep
                      ? 'border-2 border-dataops-primary-light-blue bg-white'
                      : 'border-2 border-gray-300 bg-white',
                  )}
                >
                  {step.id < currentStep ? (
                    <CheckCircleIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  ) : (
                    <span
                      className={classNames(
                        'text-sm font-medium',
                        step.id === currentStep ? 'text-dataops-primary-light-blue' : 'text-gray-500',
                      )}
                    >
                      {step.id}
                    </span>
                  )}
                </span>
              </span>
              <span className="mt-2 flex flex-col items-center">
                <span
                  className={classNames(
                    'text-sm font-medium',
                    step.id <= currentStep ? 'text-dataops-secondary-dark-blue' : 'text-gray-500',
                  )}
                >
                  {step.name}
                </span>
                <span className="text-xs text-gray-500 text-center max-w-[120px]">{step.description}</span>
              </span>
            </div>
          </li>
        ))}
      </ol>
    </nav>
  );
}
